import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from '@chakra-ui/react';

import backend from '../backend';

export type PedidoDocumento = {
  id: number;
  pedido_id: number;
  pedido_envio_id?: number | null;
  tipo: 'etiqueta_envio' | 'nfe_xml' | 'nfe_danfe' | string;
  origem?: string | null;
  marketplace?: string | null;
  status?: string | null;
  formato?: string | null;
  nome_arquivo?: string | null;
  arquivo_url?: string | null;
  mime_type?: string | null;
  tamanho_bytes?: number | null;
  created_at?: string | null;
};

type Params = {
  pedidoId?: string | number;
};

const baixarBlob = (blob: Blob, nomeArquivo: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
};

const abrirBlob = (blob: Blob) => {
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
};

export function usePedidoDocumentos({ pedidoId }: Params) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['pedido-documentos', pedidoId];

  const documentosQuery = useQuery<PedidoDocumento[]>(
    queryKey,
    async () => {
      const response = await backend.get(`/pedidos/${pedidoId}/documentos`);

      return response.data?.data || [];
    },
    {
      enabled: Boolean(pedidoId),
    },
  );

  const anexarArquivo = async (tipo: 'xml' | 'danfe', arquivo: File) => {
    const formData = new FormData();
    formData.append('arquivo', arquivo);

    const endpoint =
      tipo === 'xml'
        ? `/pedidos/${pedidoId}/documentos/nfe/xml`
        : `/pedidos/${pedidoId}/documentos/nfe/danfe`;

    const response = await backend.post(endpoint, formData);

    return response.data;
  };

  const anexarXmlMutation = useMutation(
    async (arquivo: File) => anexarArquivo('xml', arquivo),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);

        toast({
          title: 'XML anexado com sucesso.',
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      },
      onError: () => {
        toast({
          title: 'Não foi possível anexar o XML.',
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      },
    },
  );

  const anexarDanfeMutation = useMutation(
    async (arquivo: File) => anexarArquivo('danfe', arquivo),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);

        toast({
          title: 'DANFE anexado com sucesso.',
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      },
      onError: () => {
        toast({
          title: 'Não foi possível anexar o DANFE.',
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      },
    },
  );

  const baixarDocumentoMutation = useMutation(
    async (documento: PedidoDocumento) => {
      const response = await backend.get(
        `/pedidos/${pedidoId}/documentos/${documento.id}/download`,
        { responseType: 'blob' },
      );

      baixarBlob(
        response.data,
        documento.nome_arquivo || `documento-${documento.id}`,
      );
    },
    {
      onError: () => {
        toast({
          title: 'Não foi possível baixar o documento.',
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      },
    },
  );

  const imprimirEtiquetaMutation = useMutation(
    async () => {
      const response = await backend.get(
        `/pedidos/${pedidoId}/documentos/etiqueta`,
        { responseType: 'blob' },
      );

      abrirBlob(response.data);
      queryClient.invalidateQueries(queryKey);
    },
    {
      onError: () => {
        toast({
          title: 'Etiqueta ainda não disponível para este envio.',
          status: 'warning',
          position: 'bottom-right',
          isClosable: true,
        });
      },
    },
  );

  return {
    documentos: documentosQuery.data || [],
    carregandoDocumentos: documentosQuery.isLoading,
    anexandoXml: anexarXmlMutation.isLoading,
    anexandoDanfe: anexarDanfeMutation.isLoading,
    baixandoDocumento: baixarDocumentoMutation.isLoading,
    imprimindoEtiqueta: imprimirEtiquetaMutation.isLoading,

    recarregarDocumentos: documentosQuery.refetch,
    anexarXml: anexarXmlMutation.mutateAsync,
    anexarDanfe: anexarDanfeMutation.mutateAsync,
    baixarDocumento: baixarDocumentoMutation.mutateAsync,
    imprimirEtiqueta: imprimirEtiquetaMutation.mutateAsync,
  };
}
